/**
 * <load-more-paginator>
 *
 * Click-based pagination for collection and search grids.
 * Fetches the next paginated page via section_id rendering, appends new
 * items to the grid, updates the loaded/total counter, and the URL.
 *
 * Required dataset:
 *   data-grid-selector   — CSS selector for the grid container to append into
 *   data-section-id      — Shopify section id for section_id render
 *   data-next-url        — full URL of the next page (?page=N)
 *   data-loaded          — number of items currently visible
 *   data-total           — total items in the collection / search result
 *
 * Optional dataset:
 *   data-item-selector   — nodes to append (default: .collection-grid__product-cell)
 *   data-append-mode     — "products" | "articles-columns" (default: products)
 *   data-search-type     — search result type when data-page-mode="search"
 *   data-per-page        — page size for fallback URL building
 */

function formatLoadMoreCounter(paginator, shown, total) {
  const template = paginator?.dataset?.counterTemplate;
  if (template) {
    return template.replaceAll('__COUNT__', String(shown)).replaceAll('__TOTAL__', String(total));
  }

  return `${shown}/${total}`;
}

export { formatLoadMoreCounter };

class LoadMorePaginator extends HTMLElement {
  #button = null;
  #buttonTexts = [];
  #counter = null;
  #grid = null;
  #loading = false;
  #abortController = null;
  #labelLoadMore = 'Load more';
  #labelLoading = 'Loading...';
  #boundOnClick = null;

  connectedCallback() {
    this.#button =
      this.querySelector('.LoadMore--Button, .collection-grid__load-more-btn') ??
      this.querySelector('button[type="button"]');
    this.#buttonTexts = Array.from(this.querySelectorAll('.LoadMore--ButtonText'));
    this.#counter = this.querySelector('.LoadMore--Counter');
    this.#grid = document.querySelector(this.dataset.gridSelector);

    this.#labelLoadMore = this.dataset.labelLoadMore || 'Load more';
    this.#labelLoading = this.dataset.labelLoading || 'Loading...';

    if (!this.#button || !this.#grid) {
      return;
    }

    this.#boundOnClick ??= (event) => this.#onClick(event);
    this.#button.removeEventListener('click', this.#boundOnClick);
    this.#button.addEventListener('click', this.#boundOnClick);

    if (!this.dataset.nextUrl) {
      const fallbackUrl = this.#buildNextPageUrl();
      if (fallbackUrl) {
        this.dataset.nextUrl = fallbackUrl;
        this.removeAttribute('data-exhausted');
        this.#button.removeAttribute('disabled');
      }
    }

    if (window.__collectionSnapshotRestored) {
      this.#syncGridCounts();
    }
  }

  disconnectedCallback() {
    if (this.#boundOnClick) this.#button?.removeEventListener('click', this.#boundOnClick);
    this.#abortController?.abort();
  }

  async #onClick(event) {
    event.preventDefault();
    if (this.#loading) return;

    const nextUrl = this.dataset.nextUrl || this.#buildNextPageUrl();
    const sectionId = this.dataset.sectionId;
    if (!nextUrl || !sectionId) return;

    if (nextUrl !== this.dataset.nextUrl) {
      this.dataset.nextUrl = nextUrl;
    }

    this.#setLoading(true);

    try {
      this.#abortController = new AbortController();

      const fetchUrl = new URL(nextUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(fetchUrl.toString(), { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector(this.dataset.gridSelector);
      const newPaginator = doc.querySelector('load-more-paginator');
      let appendedCount = 0;

      if (newGrid) {
        appendedCount = this.#appendItems(newGrid);
      }

      if (appendedCount === 0) {
        if (newPaginator?.dataset.nextUrl) {
          this.#updateCounter(newPaginator, 0);
        }
        return;
      }

      this.#updateCounter(newPaginator, appendedCount);

      this.dispatchEvent(
        new CustomEvent('load-more:appended', {
          bubbles: true,
          detail: {
            loaded: this.dataset.loaded,
            total: this.dataset.total,
          },
        })
      );
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('[load-more-paginator]', error);
    } finally {
      this.#setLoading(false);
    }
  }

  #appendItems(newGrid) {
    const appendMode = this.dataset.appendMode || 'products';

    if (appendMode === 'articles-columns') {
      return this.#appendArticleColumns(newGrid);
    }

    const itemSelector = this.dataset.itemSelector || '.collection-grid__product-cell';
    const appendedNodes = Array.from(newGrid.querySelectorAll(itemSelector));

    if (!appendedNodes.length) {
      return 0;
    }

    const fragment = document.createDocumentFragment();
    appendedNodes.forEach((node) => fragment.appendChild(node));
    this.#grid.appendChild(fragment);

    return appendedNodes.length;
  }

  #appendArticleColumns(newGrid) {
    const newColumns = newGrid.querySelectorAll('.search-articles-grid__column');
    const currentColumns = this.#grid.querySelectorAll('.search-articles-grid__column');

    if (!newColumns.length || newColumns.length !== currentColumns.length) {
      return 0;
    }

    let appendedCount = 0;
    const itemSelector = this.dataset.itemSelector || '.search-articles-grid__item';

    newColumns.forEach((newColumn, index) => {
      const currentColumn = currentColumns[index];
      if (!currentColumn) {
        return;
      }

      const items = Array.from(newColumn.querySelectorAll(itemSelector));
      items.forEach((node) => {
        currentColumn.appendChild(node);
        appendedCount += 1;
      });
    });

    return appendedCount;
  }

  #getItemSelector() {
    return this.dataset.itemSelector || '.collection-grid__product-cell';
  }

  #getSearchType() {
    return (
      this.dataset.searchType ||
      document.getElementById('mainGrid')?.dataset.searchResultType ||
      new URL(window.location.href).searchParams.get('type') ||
      'product'
    );
  }

  #getPerPage() {
    const fromPaginator = parseInt(this.dataset.perPage ?? '', 10);
    if (Number.isFinite(fromPaginator) && fromPaginator > 0) {
      return fromPaginator;
    }

    const mainGrid = document.getElementById('mainGrid');
    const fromGrid = parseInt(mainGrid?.dataset.productsPerPage ?? '', 10);
    return Number.isFinite(fromGrid) && fromGrid > 0 ? fromGrid : 0;
  }

  #setLoading(isLoading) {
    this.#loading = isLoading;
    const label = isLoading ? this.#labelLoading : this.#labelLoadMore;
    this.#buttonTexts.forEach((node) => {
      node.textContent = label;
    });
    if (!this.#button) {
      return;
    }
    // is-loading drives the shared button state in base.css: filled background,
    // hidden label and the spinning .itemLoader on top.
    this.#button.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      this.setAttribute('data-loading', 'true');
      this.#button.setAttribute('aria-busy', 'true');
      this.#button.setAttribute('disabled', '');
    } else {
      this.removeAttribute('data-loading');
      this.#button.removeAttribute('aria-busy');
      if (!this.hasAttribute('data-exhausted')) {
        this.#button.removeAttribute('disabled');
      }
    }
  }

  #updateCounter(newPaginator, appendedCount = 0) {
    if (newPaginator) {
      this.dataset.total = newPaginator.dataset.total || this.dataset.total;
      this.dataset.loaded = newPaginator.dataset.loaded || this.dataset.loaded;
      this.dataset.nextUrl = newPaginator.dataset.nextUrl || '';
      this.#reconcileTotalWithLoaded(this.#getVisibleProductCount() || parseInt(this.dataset.loaded ?? '0', 10));
    } else if (appendedCount > 0) {
      const visibleCount = this.#getVisibleProductCount();
      const nextLoaded = visibleCount > 0 ? visibleCount : this.#getLoadedProductCount() + appendedCount;
      this.dataset.loaded = String(nextLoaded);
      this.#reconcileTotalWithLoaded(nextLoaded);
      this.dataset.nextUrl = '';
    }

    this.#syncGridCounts();

    const loaded = this.#getLoadedProductCount();
    const totalCount = parseInt(this.dataset.total ?? '0', 10);

    if (loaded >= totalCount) {
      this.dataset.nextUrl = '';
      this.#disableExhausted();
      return;
    }

    if (this.dataset.nextUrl) {
      this.removeAttribute('data-exhausted');
      this.#button?.removeAttribute('disabled');
      return;
    }

    const fallbackUrl = this.#buildNextPageUrl();
    if (fallbackUrl) {
      this.dataset.nextUrl = fallbackUrl;
      this.removeAttribute('data-exhausted');
      this.#button?.removeAttribute('disabled');
      return;
    }

    this.#disableExhausted();
  }

  #reconcileTotalWithLoaded(loadedCount) {
    if (!Number.isFinite(loadedCount) || loadedCount <= 0) {
      return;
    }

    const total = parseInt(this.dataset.total ?? '0', 10);
    const hasNextPage = Boolean(this.dataset.nextUrl);

    if (!Number.isFinite(total) || loadedCount <= total) {
      return;
    }

    this.dataset.total = String(hasNextPage ? Math.max(loadedCount + 1, total) : loadedCount);
  }

  #getLoadedProductCount() {
    const visibleCount = this.#getVisibleProductCount();
    const loaded = parseInt(this.dataset.loaded ?? '0', 10);

    if (Number.isFinite(visibleCount) && visibleCount > 0) {
      return visibleCount;
    }

    if (!Number.isFinite(loaded)) {
      return 0;
    }

    return loaded;
  }

  #buildNextPageUrl() {
    const loaded = this.#getLoadedProductCount();
    const total = parseInt(this.dataset.total ?? '0', 10);

    if (!Number.isFinite(total) || loaded >= total) {
      return '';
    }

    const perPage = this.#getPerPage();
    const currentPage = perPage > 0 ? Math.max(1, Math.ceil(loaded / perPage)) : 1;
    const nextPage = currentPage + 1;
    const maxPage = perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1;

    if (nextPage > maxPage) {
      return '';
    }

    if (this.dataset.pageMode === 'search') {
      const fetchUrl = new URL(window.location.href);
      fetchUrl.searchParams.set('page', String(nextPage));
      fetchUrl.searchParams.delete('section_id');

      return fetchUrl.pathname + fetchUrl.search;
    }

    const fetchUrl = new URL(window.location.href);
    fetchUrl.searchParams.set('page', String(nextPage));
    fetchUrl.searchParams.delete('section_id');

    return fetchUrl.pathname + fetchUrl.search;
  }

  #getVisibleProductCount() {
    if (!this.#grid) {
      return 0;
    }

    const itemSelector = this.#getItemSelector();
    return this.#grid.querySelectorAll(itemSelector).length || this.#grid.children.length;
  }

  #syncGridCounts() {
    const mainGrid = document.getElementById('mainGrid');
    if (!mainGrid) {
      return;
    }

    const loadedItems = this.#getLoadedProductCount();
    this.#reconcileTotalWithLoaded(loadedItems);

    const total = parseInt(this.dataset.total ?? mainGrid.dataset.productCount ?? mainGrid.dataset.articleCount ?? '0', 10);
    const reconciledTotal = Number.isFinite(total) && loadedItems > total ? loadedItems : total;
    const shownText = String(loadedItems);
    const totalText = Number.isFinite(reconciledTotal)
      ? String(reconciledTotal)
      : (mainGrid.dataset.productCount ?? mainGrid.dataset.articleCount ?? '');
    const searchResultType = mainGrid.dataset.searchResultType || this.#getSearchType();

    this.dataset.loaded = shownText;

    if (searchResultType === 'article') {
      mainGrid.dataset.articleCount = totalText !== '' ? totalText : mainGrid.dataset.articleCount;
    } else {
      mainGrid.dataset.productsShown = shownText;
      if (totalText !== '') {
        mainGrid.dataset.productCount = totalText;
      }
    }

    if (totalText !== '') {
      this.dataset.total = totalText;
    }

    if (this.#counter && totalText !== '') {
      this.#counter.textContent = formatLoadMoreCounter(this, shownText, totalText);
    }

    this.#updateToolbarCounts(shownText, totalText);

    document.dispatchEvent(
      new CustomEvent('collection-grid:counts-updated', {
        bubbles: true,
        detail: {
          loaded: loadedItems,
          total: totalText !== '' ? parseInt(totalText, 10) : undefined,
        },
      })
    );
  }

  #updateToolbarCounts(shown, total) {
    document.querySelectorAll('[data-toolbar-products-shown]').forEach((el) => {
      el.textContent = shown;
    });

    document.querySelectorAll('[data-toolbar-products-total]').forEach((el) => {
      if (total !== '') {
        el.textContent = total;
      }
    });
  }

  #disableExhausted() {
    this.dataset.nextUrl = '';
    this.setAttribute('data-exhausted', 'true');
    this.#button?.setAttribute('disabled', '');
  }
}

if (!customElements.get('load-more-paginator')) {
  customElements.define('load-more-paginator', LoadMorePaginator);
}
